import DOMPurify from 'dompurify';

/**
 * The trust boundary shared by every pipe that hands HTML to `[innerHTML]`.
 *
 * Each caller decides which tags and attributes its own feature needs; what stays here is the part
 * that must not drift between them — how a URL is judged reachable, and how text is escaped before
 * it is put next to markup.
 */
const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
};

/** Reachable and inert: no `javascript:`, and no relative path that could point back into the portal. */
export const ALLOWED_URI_REGEXP: RegExp = /^(?:https?:|mailto:)/i;

/**
 * DOMPurify tests every attribute it does not already know to be URI-free against
 * `ALLOWED_URI_REGEXP`, which the literal values `_blank` and `noopener noreferrer` fail. Neither
 * attribute can carry a URI, so exempting them costs nothing.
 */
export const URI_SAFE_ATTR: string[] = ['target', 'rel'];

/**
 * Strips everything outside the given whitelist, then reports the result as a string.
 *
 * The caller is still responsible for trusting the output — this only makes the output worth
 * trusting.
 */
export function sanitizeHtml(html: string, allowedTags: string[], allowedAttr: string[]): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: allowedAttr,
    ALLOWED_URI_REGEXP,
    ADD_URI_SAFE_ATTR: URI_SAFE_ATTR,
  });
}

/** Renders text inert wherever it lands, whether that is a text node or an attribute value. */
export function escapeHtml(value: string): string {
  return value.replace(/[&<>"]/g, (character: string): string => HTML_ESCAPES[character]);
}
